import React, { useEffect, useMemo, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteProduct,
  getProductsError,
  getProductsStatus,
  selectAllProducts,
  getProductsSuccess,
  updateStatus,
  getProductByCategoryId,
} from "../../../../features/productSlice";
import AppBarSearch from "../../../../global/appBarSearch";
import AlertDialog from "../../../../components/alertDialog";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import { useRestaurant } from "../../../../hooks/RestaurantContext";
import SetDiscountDialog from "../../product/setDiscount";
import ListViewProducts from "./listViewProducts";
import CardViewProduct from "./cardViewProduct";
import MenuItemFilter from "../../../../components/filters/menuItemFilter";
import { StockList, VisibilityList } from "../../../../models/product.model";
import {
  ResponsiveProvider,
  useIsCompactHeader,
} from "../../../../hooks/uiHook";
import { useTranslation } from "react-i18next";
import BackButton from "../../../../components/backButton";
const ProductsByCategory = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantId } = useRestaurant();
  const isCompactHeader = useIsCompactHeader();
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [localProducts, setLocalProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const status = useSelector(getProductsStatus);
  const error = useSelector(getProductsError);
  const products = useSelector(selectAllProducts);
  const success = useSelector(getProductsSuccess);
  const categoryId = location.state.categoryId;

  const handleClickOpen = (cardId) => {
    setOpen(true);
    setCardId(cardId);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("/products/add");
  };

  const handleModify = (data) => {
    navigate("/products/modify", {
      state: {
        product: data,
      },
    });
  };

  useEffect(() => {
    if (categoryId && restaurantId) {
      dispatch(getProductByCategoryId({ categoryId, restaurantId }));
    }
  }, [categoryId, dispatch, restaurantId]);

  useEffect(() => {
    if (status === "fetchDataByCategory") setLocalProducts(products);
  }, [products, status]);

  const filteredProductsMemo = useMemo(() => {
    let filtered = localProducts || [];

    const query = search.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter((product) => {
        const name = (product.name || "").toLowerCase();
        const description = (product.description || "").toLowerCase();
        return name.includes(query) || description.includes(query);
      });
    }

    if (visibilityFilter !== "") {
      filtered = filtered.filter(
        (product) => String(product.visible) === String(visibilityFilter)
      );
    }

    if (stockFilter !== "") {
      filtered = filtered.filter(
        (product) => String(product.outOfStock) === String(stockFilter)
      );
    }

    return filtered;
  }, [localProducts, search, stockFilter, visibilityFilter]);

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success);
      setLocalProducts((prev) => prev.filter((item) => item._id !== cardId));
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
    }
  }, [cardId, dispatch, error, localProducts, status, success]);

  useEffect(() => {
    if (status === "discountSetSuccess") {
      dispatch(updateStatus());
      dispatch(getProductByCategoryId({ categoryId, restaurantId }));
    } else if (status === "discountSetError") {
      toast.error(error);
    }
    if (status === "discountRemoveSuccess") {
      dispatch(updateStatus());
      dispatch(getProductByCategoryId({ categoryId, restaurantId }));
    } else if (status === "discountRemoveError") {
      toast.error(error);
    }
  }, [dispatch, error, status, success, categoryId, restaurantId]);

  const handleDiscount = (product) => {
    setSelectedProduct(product);
    setDiscountDialogOpen(true);
  };

  const effectiveViewMode = isCompactHeader ? "grid" : viewMode;

  return (
    <div className="main-application">
      <CssBaseline />
      <BackButton />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          searchText={t("search.byNameOrDescription")}
          handleSubmit={handleSubmit}
          handleSearch={(e) => setSearch(e.target.value)}
          title={t("pages.myProducts")}
          buttonTitle={t("actions.addProduct")}
          viewMode={effectiveViewMode}
          onToggleViewMode={
            isCompactHeader
              ? undefined
              : () => setViewMode(viewMode === "list" ? "grid" : "list")
          }
          children={
            <>
              <MenuItemFilter
                label={t("filters.allVisibilities")}
                value={visibilityFilter}
                onChange={(e) => setVisibilityFilter(e.target.value)}
                filters={VisibilityList}
              />
              <MenuItemFilter
                label={t("filters.allStocks")}
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                filters={StockList}
              />
            </>
          }
        />
      </ResponsiveProvider>
      <main>
        <Box sx={{ my: 2 }}>
          {effectiveViewMode === "list" ? (
            <ListViewProducts
              handleClickOpen={handleClickOpen}
              handleModify={handleModify}
              handleDiscount={handleDiscount}
              filteredProducts={filteredProductsMemo}
              setFilteredProducts={setLocalProducts}
              status={status}
              error={error}
              search={search}
            />
          ) : (
            <CardViewProduct
              filteredProducts={filteredProductsMemo}
              handleModify={handleModify}
              handleDiscount={handleDiscount}
              handleClickOpen={handleClickOpen}
              status={status}
              error={error}
              search={search}
            />
          )}
          <AlertDialog
            handleClose={() => setOpen(false)}
            open={open}
            name={"produit"}
            cardId={cardId}
            deleteData={() => dispatch(deleteProduct({ productId:cardId, restaurantId }))}
          />
          <SetDiscountDialog
            open={discountDialogOpen}
            onClose={() => {
              setDiscountDialogOpen(false);
              setSelectedProduct(null);
            }}
            product={selectedProduct}
          />
        </Box>
      </main>
    </div>
  );
};

export default ProductsByCategory;
